import { AtmosphereDef, CelestialBodyDef, SurfacePalette, TerrainProfile } from '../BodyDef';
import { HOURS, orbitKm } from './orbitHelpers';

/**
 * Moons of the Cyrene system.
 *
 * Moons reuse the same definition shape as planets, so the terrain, atmosphere
 * and gameplay systems treat them identically. `makeMoon` only exists to keep
 * the airless-body defaults from being restated a dozen times; every field it
 * fills can be overridden per moon.
 */

const AIRLESS_PALETTE: SurfacePalette = {
  deepWater: [0.04, 0.04, 0.05],
  shallowWater: [0.08, 0.08, 0.09],
  shore: [0.3, 0.29, 0.28],
  lowland: [0.22, 0.21, 0.2],
  midland: [0.32, 0.31, 0.3],
  highland: [0.44, 0.43, 0.42],
  peak: [0.62, 0.61, 0.6],
  nightEmissive: [0, 0, 0],
  nightEmissiveStrength: 0,
};

const CRATERED_TERRAIN: TerrainProfile = {
  maxElevation: 3400,
  seaLevelFraction: -1,
  oceanCoverage: 0,
  continentScale: 8.0e5,
  plateCount: 0,
  ridgeStrength: 0,
  mountainStrength: 0.3,
  mountainScale: 1.2e5,
  warpStrength: 0.14,
  detailStrength: 0.42,
  detailScale: 420,
  erosion: 0.02,
  craterDensity: 0.95,
  craterMaxRadius: 34000,
  duneStrength: 0,
  duneScale: 1,
  fissureStrength: 0.05,
  roughness: 1.15,
};

interface MoonSpec {
  id: string;
  name: string;
  parentId: string;
  radius: number;
  surfaceGravity: number;
  rotationPeriodHours: number;
  orbitKm: number;
  eccentricity: number;
  inclinationDeg: number;
  meanAnomalyDeg: number;
  surfaceClass: CelestialBodyDef['surfaceClass'];
  meanTemperature: number;
  biomes: string[];
  description: string;
  markerColor: [number, number, number];
  explorable?: boolean;
  atmosphere?: AtmosphereDef | null;
  terrain?: Partial<TerrainProfile>;
  palette?: Partial<SurfacePalette>;
  radiation?: number;
  seismic?: number;
  settlementDensity?: number;
  lifePotential?: number;
  hazardNotes?: string[];
}

function makeMoon(spec: MoonSpec): CelestialBodyDef {
  return {
    id: spec.id,
    name: spec.name,
    kind: 'moon',
    parentId: spec.parentId,
    radius: spec.radius,
    surfaceGravity: spec.surfaceGravity,
    rotationPeriod: spec.rotationPeriodHours * HOURS,
    axialTilt: 0,
    epochSpin: (spec.id.length * 0.7) % (Math.PI * 2),
    orbit: orbitKm(spec.orbitKm, spec.eccentricity, spec.inclinationDeg, 0, 0, spec.meanAnomalyDeg),
    surfaceClass: spec.surfaceClass,
    explorable: spec.explorable ?? true,
    atmosphere: spec.atmosphere ?? null,
    thermal: {
      meanSurfaceTemperature: spec.meanTemperature,
      polarDelta: 40,
      diurnalRange: spec.atmosphere ? 20 : 130,
      lapseRate: spec.atmosphere ? 0.004 : 0,
    },
    hazards: {
      radiation: spec.radiation ?? 0.0006,
      stormFrequency: spec.atmosphere ? 0.3 : 0,
      seismicActivity: spec.seismic ?? 0.05,
      corrosion: 0,
      notes: spec.hazardNotes ?? ['No atmosphere: full pressure suit and thermal regulation required.'],
    },
    terrain: { ...CRATERED_TERRAIN, ...spec.terrain },
    palette: { ...AIRLESS_PALETTE, ...spec.palette },
    biomes: spec.biomes,
    settlementDensity: spec.settlementDensity ?? 0.05,
    lifePotential: spec.lifePotential ?? 0,
    description: spec.description,
    markerColor: spec.markerColor,
  };
}

export const MOONS: CelestialBodyDef[] = [
  // --- Halcyon ------------------------------------------------------------
  makeMoon({
    id: 'lume',
    name: 'Lume',
    parentId: 'halcyon',
    radius: 1.58e6,
    surfaceGravity: 1.42,
    rotationPeriodHours: 27.1 * 24,
    orbitKm: 372000,
    eccentricity: 0.0421,
    inclinationDeg: 5.1,
    meanAnomalyDeg: 62,
    surfaceClass: 'rocky',
    meanTemperature: 218,
    biomes: ['regolith', 'rockyDesert', 'cave', 'crystalField'],
    settlementDensity: 0.4,
    description:
      'Halcyon\'s single large moon and the training ground for every pilot in the system. Tidally locked, ' +
      'airless, and close enough that a mistake is survivable. Its regolith holds three centuries of accumulated ' +
      'helium-3 and rather more crashed hardware than the registry admits.',
    markerColor: [0.78, 0.78, 0.74],
  }),

  // --- Thalassa -----------------------------------------------------------
  makeMoon({
    id: 'nerea',
    name: 'Nerea',
    parentId: 'thalassa',
    radius: 9.1e5,
    surfaceGravity: 0.86,
    rotationPeriodHours: 61.4,
    orbitKm: 214000,
    eccentricity: 0.0091,
    inclinationDeg: 1.2,
    meanAnomalyDeg: 190,
    surfaceClass: 'frozen',
    meanTemperature: 141,
    biomes: ['iceField', 'glacier', 'cave'],
    terrain: { craterDensity: 0.5, maxElevation: 1800, fissureStrength: 0.44, detailStrength: 0.3 },
    palette: { lowland: [0.7, 0.76, 0.8], midland: [0.78, 0.83, 0.88], peak: [0.94, 0.97, 1.0] },
    description:
      'A cracked ice shell over a briny subsurface ocean, resurfaced often enough that impact craters are rare. ' +
      'The fracture network vents water vapour on a tidal schedule precise enough to set a clock by.',
    markerColor: [0.82, 0.9, 0.94],
  }),

  // --- Kharis -------------------------------------------------------------
  makeMoon({
    id: 'sef',
    name: 'Sef',
    parentId: 'kharis',
    radius: 4.2e5,
    surfaceGravity: 0.31,
    rotationPeriodHours: 18.2,
    orbitKm: 41000,
    eccentricity: 0.0022,
    inclinationDeg: 0.9,
    meanAnomalyDeg: 12,
    surfaceClass: 'rocky',
    meanTemperature: 196,
    biomes: ['regolith', 'rockyDesert'],
    terrain: { maxElevation: 1100, craterDensity: 0.99, craterMaxRadius: 9000 },
    settlementDensity: 0.12,
    description:
      'A captured asteroid barely large enough to be round, in a low, fast orbit. Its negligible gravity makes it ' +
      'the cheapest orbital transfer node at Kharis, and the shipbreaking yards there reflect that.',
    markerColor: [0.6, 0.52, 0.46],
  }),

  // --- Orphys -------------------------------------------------------------
  makeMoon({
    id: 'vault',
    name: 'Vault',
    parentId: 'orphys',
    radius: 7.0e5,
    surfaceGravity: 0.64,
    rotationPeriodHours: 88.4,
    orbitKm: 186000,
    eccentricity: 0.0134,
    inclinationDeg: 3.4,
    meanAnomalyDeg: 274,
    surfaceClass: 'rocky',
    meanTemperature: 172,
    biomes: ['regolith', 'cave', 'crystalField', 'radioactive'],
    terrain: { craterDensity: 0.7, fissureStrength: 0.3, maxElevation: 2600 },
    radiation: 0.014,
    settlementDensity: 0.03,
    description:
      'A small, unusually dense moon with a fission-warm interior. Its cave systems are hot, radioactive and, ' +
      'according to two independent surveys, not entirely natural in their geometry.',
    markerColor: [0.48, 0.5, 0.44],
  }),

  // --- Aegis --------------------------------------------------------------
  makeMoon({
    id: 'ferrum',
    name: 'Ferrum',
    parentId: 'aegis',
    radius: 2.12e6,
    surfaceGravity: 1.94,
    rotationPeriodHours: 42.6,
    orbitKm: 421000,
    eccentricity: 0.0041,
    inclinationDeg: 0.4,
    meanAnomalyDeg: 33,
    surfaceClass: 'rocky',
    meanTemperature: 118,
    biomes: ['regolith', 'rockyDesert', 'cave', 'crystalField'],
    terrain: { craterDensity: 0.55, mountainStrength: 0.5, maxElevation: 4800, roughness: 1.3 },
    palette: { lowland: [0.3, 0.26, 0.22], midland: [0.4, 0.35, 0.29], peak: [0.6, 0.56, 0.5] },
    radiation: 0.11,
    settlementDensity: 0.35,
    hazardNotes: [
      'Inside the Aegis radiation belt: shielding is mandatory for stays over four hours.',
      'No atmosphere; surface temperature swings 130 K between day and night.',
    ],
    description:
      'A stripped planetary core, all metal and no mantle. The largest single-site mining operation in the ' +
      'system runs here, entirely automated because the radiation belt makes crewed shifts uneconomic.',
    markerColor: [0.68, 0.56, 0.44],
  }),
  makeMoon({
    id: 'cinder',
    name: 'Cinder',
    parentId: 'aegis',
    radius: 1.76e6,
    surfaceGravity: 1.71,
    rotationPeriodHours: 26.9,
    orbitKm: 268000,
    eccentricity: 0.0092,
    inclinationDeg: 0.2,
    meanAnomalyDeg: 155,
    surfaceClass: 'volcanic',
    meanTemperature: 154,
    biomes: ['obsidianFlow', 'ashPlain', 'volcanic', 'cave'],
    terrain: {
      craterDensity: 0.02,
      fissureStrength: 0.95,
      erosion: 0.04,
      maxElevation: 5400,
      mountainStrength: 0.4,
      roughness: 1.35,
    },
    palette: {
      lowland: [0.36, 0.26, 0.12],
      midland: [0.48, 0.34, 0.14],
      highland: [0.3, 0.24, 0.2],
      peak: [0.62, 0.56, 0.46],
      nightEmissive: [1.0, 0.4, 0.06],
      nightEmissiveStrength: 0.7,
    },
    radiation: 0.28,
    seismic: 0.99,
    settlementDensity: 0.04,
    hazardNotes: [
      'Continuous tidal volcanism: eruption plumes reach orbital altitude.',
      'Deep inside the Aegis radiation belt.',
      'Surface features change measurably within a single visit.',
    ],
    description:
      'Tidally kneaded by Aegis into the most volcanically active body known. Sulphur plumes rise hundreds of ' +
      'kilometres and fall back as snow. Nothing built on the surface has ever lasted a full local year.',
    markerColor: [0.94, 0.72, 0.24],
  }),
  makeMoon({
    id: 'tessellate',
    name: 'Tessellate',
    parentId: 'aegis',
    radius: 2.38e6,
    surfaceGravity: 1.36,
    rotationPeriodHours: 84.2,
    orbitKm: 712000,
    eccentricity: 0.0016,
    inclinationDeg: 0.6,
    meanAnomalyDeg: 221,
    surfaceClass: 'frozen',
    meanTemperature: 106,
    biomes: ['iceField', 'glacier', 'cave', 'alienOrganic'],
    terrain: { craterDensity: 0.18, fissureStrength: 0.72, maxElevation: 2100, erosion: 0.12 },
    palette: { lowland: [0.66, 0.74, 0.82], midland: [0.76, 0.84, 0.9], peak: [0.95, 0.98, 1.0] },
    radiation: 0.06,
    lifePotential: 0.44,
    settlementDensity: 0.14,
    description:
      'An ice moon whose surface is divided into polygonal plates by a fracture network no model fully explains. ' +
      'Vent chemistry from the subsurface ocean carries complex organics, and the biological survey is ongoing ' +
      'and closely held.',
    markerColor: [0.72, 0.86, 0.92],
  }),

  // --- Kolyma -------------------------------------------------------------
  makeMoon({
    id: 'rime',
    name: 'Rime',
    parentId: 'kolyma',
    radius: 1.1e6,
    surfaceGravity: 0.72,
    rotationPeriodHours: 132.0,
    orbitKm: 244000,
    eccentricity: 0.0067,
    inclinationDeg: 2.1,
    meanAnomalyDeg: 88,
    surfaceClass: 'frozen',
    meanTemperature: 82,
    biomes: ['iceField', 'glacier', 'cave'],
    terrain: { craterDensity: 0.82, maxElevation: 1600, detailStrength: 0.24 },
    palette: { lowland: [0.74, 0.78, 0.84], midland: [0.82, 0.86, 0.9], peak: [0.96, 0.98, 1.0] },
    settlementDensity: 0.02,
    description:
      'The outermost catalogued landing site. Methane frost, long shadows, and a survey beacon that has been ' +
      'transmitting since before the current registry existed.',
    markerColor: [0.8, 0.86, 0.92],
  }),
];
